import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Shield, Wifi, AlertCircle, CheckCircle, Mail } from "lucide-react";

interface TailscaleAuthProps {
  onAuthenticated: () => void;
}

// Tailscale IP ranges (100.64.0.0/10)
const TAILSCALE_IP_RANGES = [
  /^100\.(64|65|66|67|68|69|70|71|72|73|74|75|76|77|78|79)\./, // 100.64-79.x.x
  /^100\.(8|9|10|11|12|13|14|15|16|17|18|19|20|21|22|23|24|25|26|27|28|29|30|31)\./, // 100.8-31.x.x
  /^100\.(32|33|34|35|36|37|38|39|40|41|42|43|44|45|46|47|48|49|50|51|52|53|54|55|56|57|58|59|60|61|62|63)\./, // 100.32-63.x.x
  /^100\.(96|97|98|99|100|101|102|103|104|105|106|107|108|109|110|111|112|113|114|115|116|117|118|119|120|121|122|123|124|125|126|127)\./, // 100.96-127.x.x
];

// Always authorized emails
const AUTHORIZED_EMAILS = [
  "treyanderson@gmail.com",
  "lora.pea@gmail.com",
];

export function TailscaleAuth({ onAuthenticated }: TailscaleAuthProps) {
  const [isChecking, setIsChecking] = useState(true);
  const [isOnTailscale, setIsOnTailscale] = useState(false);
  const [isAuthorizedEmail, setIsAuthorizedEmail] = useState(false);
  const [clientIP, setClientIP] = useState<string | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      setIsChecking(true);
      
      // Get client IP
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      const ip = data.ip;
      
      setClientIP(ip);
      
      // Check if IP is in Tailscale range
      const isTailscaleIP = TAILSCALE_IP_RANGES.some(range => range.test(ip));
      setIsOnTailscale(isTailscaleIP);
      
      // For now, we check IP only. In production, you could also check:
      // - Cloudflare Access JWT token (would contain email)
      // - Custom header from reverse proxy
      // - Query parameter with signed token
      
      // Since we can't easily get email from client-side alone,
      // authorized emails would typically access via a special URL
      // or have a bypass mechanism
      
      // If on Tailscale, allow access
      if (isTailscaleIP) {
        grantAccess(ip);
      } else {
        setError(`Your device (${ip}) is not connected to the family Tailscale network.`);
      }
    } catch (err) {
      console.error("Error checking access:", err);
      setError("Unable to verify network connection. Please check your internet connection.");
    } finally {
      setIsChecking(false);
    }
  };

  const grantAccess = (ip: string, email?: string) => {
    // Store auth in session
    sessionStorage.setItem("memorial-tailscale-auth", "true");
    sessionStorage.setItem("memorial-client-ip", ip);
    if (email) {
      sessionStorage.setItem("memorial-user-email", email);
    }
    
    // Small delay to show success state
    setTimeout(() => {
      onAuthenticated();
    }, 1500);
  };

  // Manual bypass for authorized users (via URL param or local storage)
  const handleAuthorizedEmailAccess = () => {
    // This would be called via a special URL or after email verification
    // For now, we'll allow manual override with a confirmation
    const confirmed = window.confirm(
      "Are you treyanderson@gmail.com or lora.pea@gmail.com?\n\n" +
      "This memorial is private and access is logged."
    );
    
    if (confirmed) {
      const email = prompt("Please enter your email address:");
      if (email && AUTHORIZED_EMAILS.includes(email.toLowerCase())) {
        setIsAuthorizedEmail(true);
        grantAccess(clientIP || "unknown", email);
      } else {
        alert("Email not authorized. Access denied.");
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-[60] flex flex-col items-center justify-center bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 p-6"
    >
      {/* Tailscale Logo / Shield */}
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mb-8"
      >
        <div className="flex h-24 w-24 items-center justify-center rounded-3xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-blue-500/30">
          <Shield className="h-12 w-12 text-white" />
        </div>
      </motion.div>

      {/* Title */}
      <motion.h1
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mb-2 text-center text-2xl font-semibold text-white"
      >
        Private Memorial
      </motion.h1>

      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mb-8 text-center text-sm text-white/50"
      >
        Protected by Tailscale
      </motion.p>

      {/* Status Card */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="w-full max-w-sm rounded-2xl bg-white/10 p-6 backdrop-blur-md"
      >
        {isChecking ? (
          <div className="flex flex-col items-center gap-4">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <Wifi className="h-8 w-8 text-blue-400" />
            </motion.div>
            <p className="text-center text-white/70">Verifying access...</p>
          </div>
        ) : isOnTailscale || isAuthorizedEmail ? (
          <div className="flex flex-col items-center gap-4">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="rounded-full bg-green-500/20 p-3"
            >
              <CheckCircle className="h-8 w-8 text-green-400" />
            </motion.div>
            <div className="text-center">
              <p className="font-medium text-white">
                {isAuthorizedEmail ? "Authorized User" : "Connected via Tailscale"}
              </p>
              <p className="mt-1 text-sm text-white/50">IP: {clientIP}</p>
              <p className="mt-3 text-sm text-green-400">Access granted</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div className="rounded-full bg-red-500/20 p-3">
              <AlertCircle className="h-8 w-8 text-red-400" />
            </div>
            <div className="text-center">
              <p className="font-medium text-white">Access Restricted</p>
              <p className="mt-2 text-sm text-white/50">{error}</p>
              <div className="mt-4 rounded-lg bg-white/5 p-3 text-left">
                <p className="text-xs font-medium text-white/70">To access this memorial:</p>
                <ol className="mt-2 list-decimal pl-4 text-xs text-white/50">
                  <li>Install Tailscale on your device</li>
                  <li>Connect to the family Tailscale network</li>
                  <li>Refresh this page</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Action Buttons */}
      {!isChecking && !isOnTailscale && !isAuthorizedEmail && (
        <div className="mt-6 flex flex-col gap-3">
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => checkAccess()}
            className="rounded-full bg-white/10 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-white/20"
          >
            Check Again
          </motion.button>
          
          {/* Authorized Email Access */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleAuthorizedEmailAccess}
            className="flex items-center justify-center gap-2 rounded-full bg-white/5 px-6 py-3 text-xs text-white/40 transition-colors hover:bg-white/10 hover:text-white/60"
          >
            <Mail className="h-3 w-3" />
            Authorized User Access
          </motion.button>
        </div>
      )}

      {/* Setup Instructions */}
      {!isChecking && !isOnTailscale && !isAuthorizedEmail && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 max-w-sm text-center"
        >
          <p className="text-xs text-white/30">
            <strong className="text-white/50">Tailscale Setup:</strong>
            <br />
            1. Download Tailscale from tailscale.com
            <br />
            2. Sign in with the family Google account
            <br />
            3. Connect to the network
          </p>
        </motion.div>
      )}

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="absolute bottom-8 text-center"
      >
        <p className="text-xs text-white/20">In loving memory</p>
      </motion.div>
    </motion.div>
  );
}

// Check if user is authenticated
export function isTailscaleAuthenticated(): boolean {
  return sessionStorage.getItem("memorial-tailscale-auth") === "true";
}

// Get stored client IP
export function getClientIP(): string | null {
  return sessionStorage.getItem("memorial-client-ip");
}

// Get stored user email
export function getUserEmail(): string | null {
  return sessionStorage.getItem("memorial-user-email");
}

// Check if user is authorized email
export function isAuthorizedUser(): boolean {
  const email = getUserEmail();
  return email ? AUTHORIZED_EMAILS.includes(email.toLowerCase()) : false;
}

// Clear authentication
export function tailscaleLogout(): void {
  sessionStorage.removeItem("memorial-tailscale-auth");
  sessionStorage.removeItem("memorial-client-ip");
  sessionStorage.removeItem("memorial-user-email");
}

// Validate IP is in Tailscale range
export function isTailscaleIP(ip: string): boolean {
  return TAILSCALE_IP_RANGES.some(range => range.test(ip));
}